import type { z } from 'zod';

/**
 * A background job, e.g. sending a mail or a nightly cleanup. The kernel or a module defines it
 * and handles it with `@JobHandler` in one of its services (docs/architecture/modules.md, Jobs).
 *
 * Jobs are stored above the companies, so payloads carry IDs and technical values only, never
 * names, emails or other personal data: the handler loads what it needs inside the company.
 */
export interface JobDefinition<T = unknown> {
  /** `<owner>.<name>`, the module ID (or `core` for the kernel) first, e.g. `hr.reminders.send`. */
  type: string;
  schema: z.ZodType<T>;
  /** Attempts before the job counts as failed. */
  maxAttempts: number;
  /** How long one attempt may take before another worker may pick the job up again. */
  timeoutSeconds: number;
  /** Runs the job on a schedule, with an empty payload. */
  schedule?: JobSchedule;
}

export interface JobSchedule {
  /** Cron expression with five fields, e.g. `0 3 * * *` for every night at 03:00. */
  cron: string;
  /**
   * `company` runs the job once per company where its module is active, `platform` once for all
   * (kernel only). Times are in Europe/Berlin.
   */
  scope: 'company' | 'platform';
}

export type JobPayload<J> = J extends JobDefinition<infer T> ? T : never;

const JOB_TYPE = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,3}$/;

export const JOB_TIME_ZONE = 'Europe/Berlin';

export function isJobType(value: string): boolean {
  return JOB_TYPE.test(value);
}

/** The owner of a job type: the module ID, `it.azure` for a submodule, or `core`. */
export function jobOwner(type: string, moduleIds: Iterable<string>): string | undefined {
  if (type.startsWith('core.')) return 'core';
  let owner: string | undefined;
  for (const id of moduleIds) {
    if (type.startsWith(`${id}.`) && id.length > (owner?.length ?? 0)) owner = id;
  }
  return owner;
}

export function defineJob<T>(
  type: string,
  schema: z.ZodType<T>,
  options: Partial<Omit<JobDefinition<T>, 'type' | 'schema'>> = {},
): JobDefinition<T> {
  if (!isJobType(type)) throw new Error(`Job type "${type}" must look like "<owner>.<name>"`);
  if (options.schedule?.scope === 'platform' && !type.startsWith('core.')) {
    throw new Error(`Job "${type}": only kernel jobs run once for the platform`);
  }
  return { type, schema, maxAttempts: 5, timeoutSeconds: 300, ...options };
}
